import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Pagination, QueryErrorAlert, Table } from '@shared/components';
import type { VehicleAmendment, VehicleAmendmentsPage } from '@/features/vehiclesearch/types/vehicleRegistration.types';
import {
    labelForAmendmentField,
    labelForAmendmentReason,
    labelForAmendmentType,
} from '@/features/vehiclesearch/utils/amendmentLabels';
import { formatMileage } from '@/features/vehiclesearch/utils/mileage';
import { formatHistoryActor } from '../../../utils/formatHistoryActor';
import { formatVrm } from '../../../utils/registrationView';
import { formatOccurredAt } from '../../../utils/timelineEntryLabels';

interface Props {
    isLoading: boolean;
    isError: boolean;
    page: VehicleAmendmentsPage | undefined;
    currentPage: number;
    onRetry: () => void;
    onPageChange: (page: number) => void;
}

const headingId = 'agreement-vehicle-amendments-heading';
const EMPTY_MESSAGE = 'No changes have been recorded for this vehicle yet.';

const formatValue = (field: string, value: string | null | undefined): string => {
    if (!value) return 'Not recorded';

    if (field === 'vrm') return formatVrm(value);

    return field === 'mileage' && Number.isFinite(Number(value)) ? formatMileage(Number(value)) : value;
};

const COLUMNS: ColumnDef<VehicleAmendment>[] = [
    {
        id: 'recordedAt',
        header: 'When',
        cell: ({ row }) => (
            <time dateTime={row.original.recordedAt} className="text-sm">
                {formatOccurredAt(row.original.recordedAt)}
            </time>
        ),
    },
    {
        id: 'amendmentType',
        header: 'Type',
        cell: ({ row }) => <span className="text-sm">{labelForAmendmentType(row.original.amendmentType)}</span>,
    },
    {
        id: 'changes',
        header: 'What changed',
        cell: ({ row }) => (
            <ul className="space-y-1 text-sm">
                {row.original.changedFields.map((field) => (
                    <li key={field}>
                        <span className="font-semibold">{labelForAmendmentField(field)}:</span>{' '}
                        {formatValue(field, row.original.oldValues[field])} to{' '}
                        {formatValue(field, row.original.newValues[field])}
                    </li>
                ))}
            </ul>
        ),
    },
    {
        id: 'reason',
        header: 'Reason',
        cell: ({ row }) => <span className="text-sm">{labelForAmendmentReason(row.original.reason)}</span>,
    },
    {
        id: 'actor',
        header: 'Who',
        cell: ({ row }) => <span className="text-sm">{formatHistoryActor(row.original)}</span>,
    },
];

/** Vehicle-scoped, not agreement-scoped: a change made from any plan on this car lists here. */
export function VehicleAmendmentLog({ isLoading, isError, page, currentPage, onRetry, onPageChange }: Props) {
    return (
        <section aria-labelledby={headingId} className="space-y-3">
            <h4 id={headingId} className="text-sm font-semibold text-mp-foreground">
                Vehicle changes
            </h4>

            <LogBody isLoading={isLoading} isError={isError} page={page} onRetry={onRetry} />

            {page && page.meta.lastPage > 1 && (
                <Pagination
                    currentPage={currentPage}
                    lastPage={page.meta.lastPage}
                    total={page.meta.total}
                    perPage={page.meta.perPage}
                    onPageChange={onPageChange}
                />
            )}
        </section>
    );
}

type BodyProps = Pick<Props, 'isLoading' | 'isError' | 'page' | 'onRetry'>;

function LogBody({ isLoading, isError, page, onRetry }: BodyProps) {
    if (isLoading) {
        return <LoadingSpinner message="Loading vehicle changes" />;
    }

    if (isError || !page) {
        return (
            <QueryErrorAlert message="Failed to load the changes recorded for this vehicle." onRetry={onRetry} />
        );
    }

    if (page.amendments.length === 0) {
        return <p className="text-sm text-mp-muted">{EMPTY_MESSAGE}</p>;
    }

    return (
        <Table<VehicleAmendment>
            data={page.amendments}
            columns={COLUMNS}
            enableSorting={false}
            emptyMessage={EMPTY_MESSAGE}
            getRowId={(row) => row.id}
        />
    );
}
